import React, { useMemo } from 'react';
import { isNil, sortBy } from 'lodash';
import { Line } from '@nivo/line';
import { useReportConfiguration } from 'components/reporting/sidebar/ConfiguredReport';
import {
  generateDefaultConfiguration,
  getColor,
  getDisplayValuesForSeries,
  onIdeaPortalSettingsPage,
  openDrawerForDrillIn,
} from '../helpers';
import AxesReset from '../custom_layers/AxesReset';
import { useChartScales } from './ChartScales';
import { BasicTooltip, Chip, TableTooltip } from '@nivo/tooltip';
import styles from './Chart.module.css';
import classNames from 'classnames';

function LineChart({ data, meta, width, height, publishing }) {
  const { get } = useReportConfiguration();
  const { margins } = useChartScales();
  const dataLabelFormat = Object.assign({}, get('dataLabelFormat'));

  const chartProps = generateDefaultConfiguration({
    configuration: get(),
    meta,
    width: width,
  });

  chartProps.theme.labels = {
    text: dataLabelFormat,
  };

  const plotNulls = get('plotNulls');
  const lineData = useMemo(() => {
    const mappedData = [];

    // Add a fake line to the beginning of the data with all of the data points
    // set to null. This ensures that the horizontal axis does not reorganize
    // itself when plot nulls is turned on/off.
    mappedData.push({
      id: null,
      data: data.map(d => ({ x: d[meta.columnKey], y: null })),
    });

    return mappedData.concat(
      meta.seriesLabels.map(series => {
        const points = data.reduce((pointsArray, datum) => {
          // datum[series] will default to 0 on the backend.
          // So if we are plotting nulls, we can use that 0 but if we are not
          // then we need need to skip the value so that we can have a continuous line.
          const displayValue = datum.hasOwnProperty(`${series}_display_value`)
            ? datum[`${series}_display_value`]
            : datum.display_value;
          if (!plotNulls && displayValue === null) return pointsArray;

          return pointsArray.concat({
            x: datum[meta.columnKey],
            y: datum[series],
            column_index: datum.column_index,
          });
        }, []);

        return {
          id: series,
          data: points,
        };
      })
    );
  }, [plotNulls, data, meta.seriesLabels, meta.columnKey]);

  const drillInEnabled = meta.drill_in?.enabled && !onIdeaPortalSettingsPage();
  function handleClick(node, event) {
    if (drillInEnabled) {
      openDrawerForDrillIn(
        node.data.column_index,
        node.serieId,
        meta,
        event.target
      );
    }
  }

  const containerId = `LineChart-${meta.bookmarkId}`;
  useCursorPointerStyleOnVoronoiMesh(containerId);
  const classes = classNames(styles.LineChartContainer, {
    [styles.clickable]: drillInEnabled,
  });

  return (
    <div className={classes} id={containerId}>
      <Line
        {...chartProps}
        margin={margins}
        width={width}
        height={height}
        data={lineData}
        isInteractive
        useMesh // must be enabled for mouse interactions to work
        enableSlices={false} // must be disabled for mouse interactions to work
        onClick={handleClick}
        crosshairType='x'
        enableGridY
        enablePoints
        pointSize={8}
        pointColor='white'
        pointBorderWidth={2}
        pointBorderColor={({ serieId }) => getColor(get(), meta, serieId)}
        curve={get('curve')}
        enablePointLabel={get('showLabels')}
        colors={line => getColor(get(), meta, line.id)}
        yFormat={' >-,'} // format y-axis values with comma https://nivo.rocks/line/
        layers={[
          'grid',
          'lines',
          'markers',
          'points',
          AxesResetWithPointsVisible,
          'areas',
          'crosshair',
          'slices',
          'mesh',
        ]}
        yScale={{
          stacked: false,
          type: 'linear',
          min: isNil(get('minValue')) ? 'auto' : get('minValue'),
          max: isNil(get('maxValue')) ? 'auto' : get('maxValue'),
        }}
        tooltip={({ point }) => (
          <BasicTooltip
            id={point.serieId}
            value={point.data.yFormatted}
            enableChip
            color={point.serieColor}
          />
        )}
        sliceTooltip={({ slice: { points } }) => {
          // Should probably memoize this down the road since this will be called
          // a ton.
          const sortedPoints = sortBy(points, 'y');
          return (
            <TableTooltip
              rows={sortedPoints.map(point => {
                const seriesDisplayLabel = getDisplayValuesForSeries(
                  meta,
                  point.serieId
                );

                return [
                  <Chip key='color' color={point.serieColor} />,
                  seriesDisplayLabel,
                  <strong key='data'>{point.data.yFormatted}</strong>,
                ];
              })}
            />
          );
        }}
      />
    </div>
  );
}

function AxesResetWithPointsVisible(props) {
  return (
    <AxesReset {...props} adjustBy={{ top: 5, right: 5, bottom: 5, left: 5 }} />
  );
}

function useCursorPointerStyleOnVoronoiMesh(containerId) {
  React.useEffect(() => {
    // Hack to change the in-line cursor style applied to the voronoi mesh in the Nivo line chart implementation.
    // This should be safe to only run once since the chart output is a non-responsive svg.
    const meshSelector = `#${containerId} svg g g:last-child rect[fill='red']`;
    const mesh = document.querySelector(meshSelector);
    mesh?.setAttribute('style', '');
    mesh?.classList?.add(styles.VoronoiMesh);
  }, [containerId]);
}

export default LineChart;
